import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export default function Modal({
  open,
  onClose,
  title,
  children,
  size = "md", // sm | md | lg
  initialFocusRef,
}) {
  const dialogRef = useRef(null);
  const overlayRef = useRef(null);

  // Prevent background scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Basic focus trap
  useEffect(() => {
    if (!open) return;
    const focusEl = initialFocusRef?.current || dialogRef.current;
    focusEl?.focus();
  }, [open, initialFocusRef]);

  if (!open) return null;

  const sizes = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
  };

  return createPortal(
    <div
      aria-hidden={!open}
      className="fixed inset-0 z-[100] flex items-center justify-center"
    >
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="bg-black/50 absolute inset-0 opacity-100 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
        tabIndex={-1}
        ref={dialogRef}
        className={`relative z-[101] w-[95%] ${sizes[size]} rounded-2xl border border-gray-200 bg-white shadow-2xl outline-none transition-all dark:border-white/10 dark:bg-navy-800`}
        style={{
          transform: "translateY(0)",
          animation: "modalIn 140ms ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 pt-4">
          {title ? (
            <h3 id="modal-title" className="text-lg font-semibold dark:text-white">
              {title}
            </h3>
          ) : (
            <div />
          )}

          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:hover:bg-white/10 dark:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-4 pb-4 pt-2 ">{children}</div>
      </div>

      {/* tiny keyframes inline so no extra CSS file required */}
      <style jsx>{`
        @keyframes modalIn {
          from {
            opacity: 0.9;
            transform: translateY(6px) scale(0.995);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>,
    document.body
  );
}
